import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { HiddenFileInput } from 'designSystem/components/HiddenFileInput'
import { useDocChatAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocChatAttachmentsContext'
import {
  DOC_CHAT_IMAGE_MIME_TYPES,
  imageFilesFromDataTransfer,
  MAX_DOC_CHAT_ATTACHMENTS,
  prepareImageAttachment,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatImageAttachments'
import { FavoriteSearch } from 'features/knowledgeDocumentViewer/utils/favoriteSearchesStorage'
import { SearchQueryHistoryEntry } from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import { ImagePlus, Search, X } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { DocChatAgentsMenu } from './DocChatAgentsMenu'
import { DocChatComposerActionButton } from './DocChatComposerActionButton'
import { DocChatModelSettingsMenu } from './DocChatModelSettingsMenu'
import { DocChatPromptEditor } from './DocChatPromptEditor'
import { SearchSuggestionsDropdown } from './SearchSuggestionsDropdown'

const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, [contenteditable="true"], [role="menuitem"]'
const CARD_GUTTER = '0.5rem'

const BoxRoot = styled.div`
  position: relative;
  z-index: 40;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 1.25rem;
  border: 0.0625rem solid var(--border);
  background: var(--muted);
  box-shadow: var(--shadow-lg);
  transition:
    border-color 100ms ease,
    box-shadow 100ms ease;

  &:focus-within {
    border-color: var(--ring);
    box-shadow:
      var(--shadow-lg),
      0 0 0 0.1875rem color-mix(in oklab, var(--ring) 50%, transparent);
  }
`

const PromptArea = styled.div`
  min-height: 5rem;
  display: flex;
  align-items: center;
  padding: 1rem ${CARD_GUTTER};
  border-bottom: 0.0625rem solid var(--border);
`

const PromptSearchIconWrapper = styled.span`
  display: inline-flex;
  margin-right: 0.5rem;
  color: var(--muted-foreground);
  flex-shrink: 0;
`

const ControlsRow = styled.div`
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: ${CARD_GUTTER};
`

const ControlsLeft = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const SubmitButtonSlot = styled.div`
  margin-inline-start: auto;
  display: flex;
  gap: 0.5rem;
`

const AttachmentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: ${CARD_GUTTER} ${CARD_GUTTER} 0;
`

const AttachmentChip = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  max-width: 12rem;
  padding: 0.25rem 0.5rem 0.25rem 0.25rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.75rem;
  background: var(--muted);
`

const AttachmentThumb = styled.img`
  width: 1.75rem;
  height: 1.75rem;
  object-fit: cover;
  border-radius: 0.5rem;
  flex-shrink: 0;
`

const AttachmentName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  color: var(--muted-foreground);
`

const AttachmentError = styled.span`
  font-size: 0.75rem;
  color: var(--destructive);
`

export interface DocChatSearchBoxProps {
  searchInput: string
  onSearchChange: (value: string) => void
  onSearchKeyDown?: (e: React.KeyboardEvent) => void
  onSearchSubmit: () => void
  placeholder: string
  submitLabel?: string
  suggestions?: SearchQueryHistoryEntry[]
  onSuggestionSelect?: (query: string) => void
  onClearHistory?: () => void
  favoriteSearches?: FavoriteSearch[]
  isSearchFavorited?: (query: string) => boolean
  onToggleSearchFavorite?: (query: string, contentFilter: string) => void
  contentFilter?: string
  requireQuery?: boolean
  submitDisabled?: boolean
  isGenerating?: boolean
  onStop?: () => void
}

export function DocChatSearchBox({
  searchInput,
  onSearchChange,
  onSearchKeyDown,
  onSearchSubmit,
  placeholder,
  submitLabel,
  suggestions,
  onSuggestionSelect,
  onClearHistory,
  favoriteSearches,
  isSearchFavorited,
  onToggleSearchFavorite,
  contentFilter,
  requireQuery = false,
  submitDisabled = false,
  isGenerating = false,
  onStop,
}: DocChatSearchBoxProps) {
  const { t } = useLingui()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { pendingAttachments, addAttachments, removeAttachment } =
    useDocChatAttachments()

  const attachFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return
      const room = MAX_DOC_CHAT_ATTACHMENTS - pendingAttachments.length
      if (room <= 0) {
        setAttachmentError(
          t`You can attach up to ${MAX_DOC_CHAT_ATTACHMENTS} images.`
        )
        return
      }
      setAttachmentError(null)
      try {
        const prepared = await Promise.all(
          files.slice(0, room).map(prepareImageAttachment)
        )
        addAttachments(prepared)
      } catch (error) {
        setAttachmentError(
          error instanceof Error
            ? error.message
            : t`Could not attach that image.`
        )
      }
    },
    [addAttachments, pendingAttachments.length, t]
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      void attachFiles(Array.from(event.target.files ?? []))
      event.target.value = ''
    },
    [attachFiles]
  )

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const files = imageFilesFromDataTransfer(event.clipboardData.items)
      if (!files.length) return
      event.preventDefault()
      void attachFiles(files)
    },
    [attachFiles]
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    if (event.dataTransfer.types.includes('Files')) event.preventDefault()
  }, [])

  const handleRootMouseDown = useCallback((event: React.MouseEvent) => {
    const target = event.target
    if (!(target instanceof HTMLElement)) return
    if (target.closest(INTERACTIVE_SELECTOR)) return
    event.preventDefault()
    editorRef.current?.focus()
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      const files = imageFilesFromDataTransfer(event.dataTransfer.items)
      if (!files.length) return
      event.preventDefault()
      void attachFiles(files)
    },
    [attachFiles]
  )

  const attachmentControl = (
    <>
      <HiddenFileInput
        ref={fileInputRef}
        accept={DOC_CHAT_IMAGE_MIME_TYPES.join(',')}
        multiple
        onChange={handleFileInputChange}
      />
      <Button
        variant='outline'
        size='icon'
        type='button'
        aria-label={t`Attach an image`}
        disabled={pendingAttachments.length >= MAX_DOC_CHAT_ATTACHMENTS}
        onClick={() => fileInputRef.current?.click()}>
        <ImagePlus />
      </Button>
    </>
  )

  const attachmentChips =
    pendingAttachments.length > 0 || attachmentError ? (
      <AttachmentRow>
        {pendingAttachments.map((attachment) => {
          const attachmentName = attachment.name
          return (
            <AttachmentChip key={attachment.id}>
              <AttachmentThumb src={attachment.previewUrl} alt='' />
              <AttachmentName title={attachmentName}>
                {attachmentName}
              </AttachmentName>
              <Button
                variant='ghostMuted'
                size='icon-xs'
                type='button'
                aria-label={t`Remove ${attachmentName}`}
                onClick={() => removeAttachment(attachment.id)}>
                <X size={12} />
              </Button>
            </AttachmentChip>
          )
        })}
        {attachmentError ? (
          <AttachmentError role='alert'>{attachmentError}</AttachmentError>
        ) : null}
      </AttachmentRow>
    ) : null

  const pinnedCount = favoriteSearches?.length ?? 0
  const recentCount = suggestions?.length ?? 0
  const totalCount = pinnedCount + recentCount
  const hasSuggestions = totalCount > 0
  const submitAriaLabel = isGenerating
    ? t`Queue message`
    : (submitLabel ?? t`Search documents`)

  const handleFocus = useCallback(() => {
    if (suggestions !== undefined) {
      setIsDropdownOpen(true)
      setHighlightedIndex(-1)
    }
  }, [suggestions])

  const handleChange = useCallback(
    (value: string) => {
      onSearchChange(value)
      if (suggestions !== undefined) {
        setIsDropdownOpen(true)
      }
    },
    [onSearchChange, suggestions]
  )

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        setIsDropdownOpen(false)
        setHighlightedIndex(-1)
      }
    }, 150)
  }, [])

  const handleSelect = useCallback(
    (query: string) => {
      onSuggestionSelect?.(query)
      setIsDropdownOpen(false)
      setHighlightedIndex(-1)
    },
    [onSuggestionSelect]
  )

  const handleClearHistory = useCallback(() => {
    onClearHistory?.()
    setIsDropdownOpen(false)
  }, [onClearHistory])

  const canSubmit =
    !submitDisabled &&
    (!requireQuery ||
      searchInput.trim().length > 0 ||
      pendingAttachments.length > 0)
  const isStopAction = isGenerating && onStop !== undefined

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return
    editorRef.current?.focus({ preventScroll: true })
    setIsDropdownOpen(false)
    setHighlightedIndex(-1)
    onSearchSubmit()
  }, [canSubmit, onSearchSubmit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.shiftKey || e.nativeEvent.isComposing)) return

      if (!isDropdownOpen || !hasSuggestions) {
        if (e.key === 'Enter') {
          e.preventDefault()
          if (!canSubmit) return
        }
        onSearchKeyDown?.(e)
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % totalCount)
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev <= 0 ? totalCount - 1 : prev - 1))
        return
      }

      if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault()
        e.stopPropagation()
        const pinned = favoriteSearches ?? []
        if (highlightedIndex < pinned.length) {
          handleSelect(pinned[highlightedIndex].query)
        } else {
          const recentIndex = highlightedIndex - pinned.length
          const filtered = (suggestions ?? []).filter(
            (s) =>
              !pinned.some(
                (p) => p.query.toLowerCase() === s.query.toLowerCase()
              )
          )
          if (filtered[recentIndex]) {
            handleSelect(filtered[recentIndex].query)
          }
        }
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (!canSubmit) return
        setIsDropdownOpen(false)
        setHighlightedIndex(-1)
        onSearchKeyDown?.(e)
        return
      }

      if (e.key === 'Escape') {
        setIsDropdownOpen(false)
        setHighlightedIndex(-1)
        return
      }

      onSearchKeyDown?.(e)
    },
    [
      isDropdownOpen,
      hasSuggestions,
      totalCount,
      suggestions,
      favoriteSearches,
      highlightedIndex,
      handleSelect,
      onSearchKeyDown,
      canSubmit,
    ]
  )

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchInput])

  const showDropdown =
    isDropdownOpen &&
    suggestions !== undefined &&
    onSuggestionSelect &&
    (hasSuggestions || !searchInput.trim())

  return (
    <BoxRoot
      ref={wrapperRef}
      onBlur={handleBlur}
      onMouseDown={handleRootMouseDown}
      onDragOver={handleDragOver}
      onDrop={handleDrop}>
      {attachmentChips}
      <PromptArea>
        <PromptSearchIconWrapper aria-hidden='true'>
          <Search size='1rem' />
        </PromptSearchIconWrapper>
        <DocChatPromptEditor
          editorRef={editorRef}
          value={searchInput}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onPaste={handlePaste}
          placeholder={placeholder}
        />
      </PromptArea>
      <ControlsRow>
        <ControlsLeft>
          {attachmentControl}
          <DocChatModelSettingsMenu />
          <DocChatAgentsMenu />
        </ControlsLeft>
        <SubmitButtonSlot>
          {isStopAction && onStop ? (
            <DocChatComposerActionButton
              isStopAction
              disabled={false}
              submitAriaLabel={submitAriaLabel}
              onClick={onStop}
            />
          ) : null}
          <DocChatComposerActionButton
            isStopAction={false}
            isQueueAction={isGenerating}
            disabled={!canSubmit}
            submitAriaLabel={submitAriaLabel}
            onClick={handleSubmit}
          />
        </SubmitButtonSlot>
      </ControlsRow>
      {showDropdown && (
        <SearchSuggestionsDropdown
          suggestions={suggestions}
          favoriteSearches={favoriteSearches}
          isSearchFavorited={isSearchFavorited}
          onToggleSearchFavorite={onToggleSearchFavorite}
          contentFilter={contentFilter}
          onSelect={handleSelect}
          onClearHistory={handleClearHistory}
          highlightedIndex={highlightedIndex}
        />
      )}
    </BoxRoot>
  )
}
